import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PoModalAction, PoModalComponent, PoPageAction, PoTableColumn } from '@po-ui/ng-components';

import { MainService } from 'src/app/app.service';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { SmartReviewGate, SmartReviewGateKey } from '../../core/interfaces/gate.interface';
import { GatesService } from './gates.service';
import { getApiErrorMessage, getHttpStatus } from '../../core/utils/http-error-message.util';

type GateGroup = 'entry' | 'entryByType' | 'execution';

interface GateDefinition {
  key: SmartReviewGateKey;
  group: GateGroup;
  label: string;
  description: string;
  tooltip: string;
  /** Efeito explícito mostrado no corpo do modal de confirmação ao pausar. */
  pauseConfirmation: string;
}

interface SmartReviewGateView extends SmartReviewGate, GateDefinition {
  updating: boolean;
  switchRenderVersion: number;
}

const GATE_GROUP_TITLES: Record<GateGroup, string> = {
  entry: 'Entrada — novos pedidos',
  entryByType: 'Entrada — por tipo de review',
  execution: 'Execução — fila atual'
};

/** Chave master: pausada, cobre os gates de entrada por tipo. */
const MASTER_ACCEPT_KEY: SmartReviewGateKey = 'accept_reviews';

const GATE_DEFINITIONS: GateDefinition[] = [
  {
    key: MASTER_ACCEPT_KEY,
    group: 'entry',
    label: 'Aceitar novos reviews (todos os tipos)',
    description: 'Bloqueia a entrada de trabalho novo de todos os tipos: criar review, gerar prompts e enviar ao assistente. Não afeta jobs já na fila.',
    tooltip: 'Interruptor geral: pausado, cobre TODOS os tipos e ignora as chaves por tipo. Usuários verão 503 ao iniciar review, gerar prompts ou enviar ao assistente.',
    pauseConfirmation: 'Todos os usuários ficarão impedidos de iniciar reviews de qualquer tipo, gerar prompts e enviar ao assistente.'
  },
  {
    key: 'accept_changeset',
    group: 'entryByType',
    label: 'Aceitar reviews de changeset',
    description: 'Bloqueia a entrada de reviews de changeset.',
    tooltip: 'Vale só para a entrada em /changesets. Processar uma issue percorre changesets internamente e não é barrado por aqui.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar reviews de changeset.'
  },
  {
    key: 'accept_comparison',
    group: 'entryByType',
    label: 'Aceitar reviews de comparação',
    description: 'Bloqueia a entrada de reviews de comparação, inclusive upload vs TFVC.',
    tooltip: 'Cobre comparação entre changesets e comparação de arquivo enviado contra o TFVC.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar reviews de comparação.'
  },
  {
    key: 'accept_standalone',
    group: 'entryByType',
    label: 'Aceitar reviews standalone',
    description: 'Bloqueia a entrada de reviews standalone — arquivo enviado sem TFVC.',
    tooltip: 'Use quando o volume de uploads estiver pesando no custo ou no storage.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar reviews standalone.'
  },
  {
    key: 'accept_issue',
    group: 'entryByType',
    label: 'Aceitar reviews de issue',
    description: 'Bloqueia a entrada de reviews de issue (todos os changesets da issue).',
    tooltip: 'É o tipo mais caro por pedido: uma issue pode arrastar vários changesets numa só execução.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar reviews de issue.'
  },
  {
    key: 'accept_pull',
    group: 'entryByType',
    label: 'Aceitar reviews de Pull Request',
    description: 'Bloqueia a entrada de reviews de Pull Request.',
    tooltip: 'Vale para o pedido pela tela e para o pipeline de CI, que chama a mesma rota a cada push.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar reviews de Pull Request, e o pipeline de CI receberá 503.'
  },
  {
    key: 'accept_commit',
    group: 'entryByType',
    label: 'Aceitar reviews de commit',
    description: 'Bloqueia a entrada de reviews de commit avulso de repositório git.',
    tooltip: 'Chave própria, e não a do Pull Request: para quem opera são duas portas de entrada distintas, e pausar uma não deve derrubar a outra.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar reviews de commit.'
  },
  {
    key: 'accept_git_comparison',
    group: 'entryByType',
    label: 'Aceitar reviews de comparação git',
    description: 'Bloqueia a entrada de comparações contra repositório git — commit vs commit e commit vs arquivo local.',
    tooltip: 'Separada da comparação do TFVC porque as dependências são outras: pausar por indisponibilidade do TFVC não pode derrubar o que vem do git.',
    pauseConfirmation: 'Os usuários não conseguirão iniciar comparações contra repositório git.'
  },
  {
    key: 'reprocess',
    group: 'entry',
    label: 'Reprocessamento',
    description: 'Bloqueia retry/reprocessar reviews já existentes.',
    tooltip: 'Usuários verão 503 ao tentar reprocessar. Não afeta a fila atual.',
    pauseConfirmation: 'Todos os usuários ficarão impedidos de reprocessar/retry de reviews.'
  },
  {
    key: 'worker_review',
    group: 'execution',
    label: 'Worker de review',
    description: 'Segura a execução de jobs de review já enfileirados (geração de diff e prompts).',
    tooltip: 'Os jobs ficam parados na fila e voltam a rodar ao despausar — nada é perdido. Use em deploy/manutenção, TFVC ou storage fora.',
    pauseConfirmation: 'Os jobs de review já enfileirados ficarão parados até retomar — nada é perdido.'
  },
  {
    key: 'worker_assistant',
    group: 'execution',
    label: 'Worker de assistente',
    description: 'Segura jobs de assistente já enfileirados — são as chamadas para DTA para fazer o code review e geração do entendimento técnico.',
    tooltip: 'Use para conter custo ou quando o DTA/ESP estiver fora. Os jobs param de ir para a IA e ficam na fila até retomar — nada é perdido.',
    pauseConfirmation: 'Os jobs já enfileirados ficarão parados (não vão para a IA) até retomar.'
  }
];

@Component({
  selector: 'app-admin-gates',
  templateUrl: './admin-gates.component.html',
  styleUrls: ['./admin-gates.component.css']
})
export class AdminGatesComponent implements OnInit {
  @ViewChild('confirmPauseModal', { static: true }) confirmPauseModal!: PoModalComponent;

  loading = false;
  pendingGate: SmartReviewGateView | null = null;

  // Arrays derivados recalculados apenas quando `gates` muda (ver setter),
  // evitando realocar a cada ciclo de change detection e re-renderizar as po-tables.
  private _gates: SmartReviewGateView[] = [];
  entryGates: SmartReviewGateView[] = [];
  entryByTypeGates: SmartReviewGateView[] = [];
  executionGates: SmartReviewGateView[] = [];

  get gates(): SmartReviewGateView[] {
    return this._gates;
  }

  set gates(next: SmartReviewGateView[]) {
    this._gates = next;
    this.entryGates = next.filter(gate => gate.group === 'entry');
    this.entryByTypeGates = next.filter(gate => gate.group === 'entryByType');
    this.executionGates = next.filter(gate => gate.group === 'execution');
  }

  readonly columns: PoTableColumn[] = [
    { property: 'label', label: 'Controle', type: 'cellTemplate', width: '420px' },
    { property: 'status', label: 'Situação', type: 'cellTemplate', width: '190px' },
    { property: 'paused', label: 'Pausar', type: 'cellTemplate', width: '130px' },
    { property: 'updatedBy', label: 'Atualizado por', type: 'cellTemplate', width: '170px' },
    { property: 'updatedAt', label: 'Atualizado em', type: 'cellTemplate', width: '180px' }
  ];

  readonly pageActions: PoPageAction[] = [
    {
      label: 'Atualizar',
      icon: 'po-icon-refresh',
      action: () => this.loadGates()
    }
  ];

  readonly confirmPausePrimaryAction: PoModalAction = {
    label: 'Pausar',
    danger: true,
    action: () => this.confirmPause()
  };

  readonly confirmPauseSecondaryAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.closeConfirmPauseModal()
  };

  constructor(
    private gatesService: GatesService,
    private mainService: MainService,
    private notification: NotificationServiceHM,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.mainService.validSession();
    // Acesso `smartreview.admin` é garantido pelo SmartReviewAdminGuard na rota pai do hub.
    this.loadGates();
  }

  get confirmPauseTitle(): string {
    return this.pendingGate ? `Pausar '${this.pendingGate.label}'?` : 'Pausar controle?';
  }

  get confirmPauseMessage(): string {
    return this.pendingGate?.pauseConfirmation ?? '';
  }

  get entryGroupTitle(): string {
    return GATE_GROUP_TITLES.entry;
  }

  get entryByTypeGroupTitle(): string {
    return GATE_GROUP_TITLES.entryByType;
  }

  get executionGroupTitle(): string {
    return GATE_GROUP_TITLES.execution;
  }

  /** Master pausado ignora as chaves por tipo — tudo está bloqueado. */
  get masterAcceptPaused(): boolean {
    return this._gates.some(gate => gate.key === MASTER_ACCEPT_KEY && gate.paused);
  }

  get masterAcceptNotice(): string {
    const label = GATE_DEFINITIONS.find(definition => definition.key === MASTER_ACCEPT_KEY)?.label ?? '';
    return `'${label}' está pausado e cobre todos os tipos abaixo — as chaves por tipo não decidem nada agora. Liberar o interruptor geral devolve a decisão a cada uma, sem alterar o valor registrado nelas.`;
  }

  /** Espelho do caso do master: com os 4 tipos pausados, nada entra mesmo com o master liberado. */
  get allEntryTypesPaused(): boolean {
    return !this.masterAcceptPaused
      && this.entryByTypeGates.length > 0
      && this.entryByTypeGates.every(gate => gate.paused);
  }

  get allEntryTypesNotice(): string {
    return 'Todos os tipos estão pausados — na prática nenhum review novo entra, mesmo com o interruptor geral liberado.';
  }

  /** Sem isso o operador leria "Liberado" numa linha de tipo que o master já bloqueia. */
  gateStatusLabel(gate: SmartReviewGateView): string {
    if (gate.paused) { return 'Pausado'; }
    if (this.isCoveredByMaster(gate)) { return 'Coberto pelo master'; }
    return 'Liberado';
  }

  gateStatusType(gate: SmartReviewGateView): string {
    if (gate.paused) { return 'danger'; }
    if (this.isCoveredByMaster(gate)) { return 'warning'; }
    return 'success';
  }

  private isCoveredByMaster(gate: SmartReviewGateView): boolean {
    return gate.group === 'entryByType' && this.masterAcceptPaused;
  }

  loadGates(): void {
    this.loading = true;
    this.gatesService.getAdminGates().subscribe({
      next: gates => {
        this.gates = this.buildGateViews(gates);
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.handleAdminError(error, 'Erro ao carregar controles do Smart Review.');
      }
    });
  }

  onGateToggle(gate: SmartReviewGateView, paused?: boolean): void {
    if (gate.updating) { return; }

    const requestedPaused = typeof paused === 'boolean' ? paused : !gate.paused;

    if (requestedPaused) {
      this.pendingGate = gate;
      this.setGateState(gate.key, gate.paused, true);
      this.confirmPauseModal.open();
      return;
    }

    this.updateGate(gate, false, true);
  }

  closeConfirmPauseModal(): void {
    const gateKey = this.pendingGate?.key;
    this.pendingGate = null;
    this.confirmPauseModal.close();

    if (gateKey) {
      this.setGateState(gateKey, false, true);
    }
  }

  confirmPause(): void {
    if (!this.pendingGate) { return; }

    const gate = this.pendingGate;
    this.pendingGate = null;
    this.confirmPauseModal.close();
    this.updateGate(gate, true, false);
  }

  private updateGate(gate: SmartReviewGateView, paused: boolean, previousPaused: boolean): void {
    this.setGateUpdating(gate.key, true);

    this.gatesService.updateAdminGate(gate.key, paused).subscribe({
      next: updatedGate => {
        this.applyUpdatedGate(updatedGate);
        const state = updatedGate.paused ? 'pausado' : 'reativado';
        this.notification.showToast(`${gate.label} ${state} com sucesso.`, 'success');
      },
      error: error => {
        this.setGateState(gate.key, previousPaused, true);
        this.setGateUpdating(gate.key, false);
        this.handleAdminError(error, 'Erro ao atualizar controle do Smart Review.');
      }
    });
  }

  private buildGateViews(gates: SmartReviewGate[]): SmartReviewGateView[] {
    const gatesByKey = new Map<SmartReviewGateKey, SmartReviewGate>(
      gates.map(gate => [gate.key, gate] as [SmartReviewGateKey, SmartReviewGate])
    );

    return GATE_DEFINITIONS.map(definition => {
      const gate = gatesByKey.get(definition.key);
      return {
        ...definition,
        paused: gate?.paused ?? false,
        updatedBy: gate?.updatedBy,
        updatedAt: gate?.updatedAt,
        updating: false,
        switchRenderVersion: 0
      };
    });
  }

  private setGateUpdating(key: SmartReviewGateKey, updating: boolean): void {
    this.gates = this.gates.map(gate =>
      gate.key === key ? { ...gate, updating } : gate
    );
  }

  private setGateState(key: SmartReviewGateKey, paused: boolean, refreshSwitch = false): void {
    this.gates = this.gates.map(gate =>
      gate.key === key
        ? {
            ...gate,
            paused,
            switchRenderVersion: refreshSwitch ? gate.switchRenderVersion + 1 : gate.switchRenderVersion
          }
        : gate
    );
  }

  private applyUpdatedGate(updatedGate: SmartReviewGate): void {
    // A ação acabou de ocorrer: se o PUT não devolver updatedBy/updatedAt,
    // carimba com o usuário/horário atuais em vez de exibir "n/d".
    const now = new Date().toISOString();
    const currentUser = this.mainService.userToken?.username;
    this.gates = this.gates.map(gate =>
      gate.key === updatedGate.key
        ? {
            ...gate,
            paused: updatedGate.paused,
            updatedBy: updatedGate.updatedBy || currentUser || gate.updatedBy,
            updatedAt: updatedGate.updatedAt || now,
            updating: false
          }
        : gate
    );
  }

  private handleAdminError(error: unknown, fallback: string): void {
    const status = getHttpStatus(error);

    if (status === 403) {
      this.notification.showToast('Acesso aos controles do Smart Review negado.', 'warning');
      this.router.navigate(['/menu/smart-review/reviews']);
      return;
    }

    if (status === 400) {
      this.notification.showToast(getApiErrorMessage(error, 'Erro de validação ao atualizar controle.'), 'warning');
      return;
    }

    this.notification.showToast(getApiErrorMessage(error, fallback), 'error');
  }
}
